import { Tab } from "@blueprintjs/core"
import { Panel } from "@src/Components/View"
import { MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from "@src/Constants"
import { colors } from "@src/theme"
import { Typography } from "@zeguro/component-library"
import * as React from "react"
import Select from "react-select"

import { usePortalSize } from "../usePortalSize"
import {
  ContentStyled,
  DesktopHeaderRow,
  Header,
  MobileHeaderRow,
  SelectContainer,
  TabsStyled,
  TabText,
} from "./styles"

interface ITabs {
  id: string
  text: string | JSX.Element
  shortText?: string
  disabled?: boolean
}

interface IProps {
  tabs: ITabs[]
  selectedTabId: string
  onTabChange: (key: string) => void
  children?: React.ReactNode
  borderColor?: string
  headTitle?: string
  headerAction?: React.ReactNode
}

const ZegTabs = ({ tabs, selectedTabId, onTabChange }: IProps) => {
  const { clientWidth } = usePortalSize()

  if (!tabs) {
    return null
  }

  const handleTabsChange = (value: string) => {
    const newTab = tabs.find((tab) => tab.id === value)
    if (!newTab?.disabled) {
      onTabChange(value)
    }
  }

  return (
    <TabsStyled
      id="TrainingTabs"
      onChange={handleTabsChange}
      selectedTabId={selectedTabId}
    >
      {tabs.map((tab) => (
        <Tab id={tab.id} key={tab.id}>
          <TabText selected={selectedTabId === tab.id} disabled={tab.disabled}>
            {(clientWidth > MOBILE_BREAKPOINT &&
              clientWidth < TABLET_BREAKPOINT &&
              tab.shortText) ||
              tab.text}
          </TabText>
        </Tab>
      ))}
    </TabsStyled>
  )
}

const ZegSelect = ({ tabs, selectedTabId, onTabChange }: IProps) => {
  const selectTabs = tabs.map((tab) => ({ value: tab.id, label: tab.text }))
  const selectedTab = selectTabs.find((tab) => tab.value === selectedTabId)

  return (
    <SelectContainer margin={"0 0 12px"}>
      <Select
        className="react-select-container"
        classNamePrefix="react-select"
        value={selectedTab}
        options={selectTabs}
        onChange={(tab) => onTabChange(tab?.value || "")}
        defaultValue={selectTabs[0]}
        isSearchable={false}
      />
    </SelectContainer>
  )
}

const ZegPanel = ({
  tabs,
  selectedTabId,
  onTabChange,
  children,
  borderColor,
  headTitle,
  headerAction,
}: IProps) => {
  const { isDesktop } = usePortalSize()

  return (
    <Panel borderColor={borderColor}>
      <Header>
        {!isDesktop ? (
          <>
            {headTitle && (
              <Typography
                variant="body1"
                weight="medium"
                color={colors.black50}
                lineHeight="30px"
              >
                {headTitle}
              </Typography>
            )}
            <MobileHeaderRow>
              <ZegSelect
                onTabChange={onTabChange}
                selectedTabId={selectedTabId}
                tabs={tabs}
              />
              {headerAction}
            </MobileHeaderRow>
          </>
        ) : (
          <DesktopHeaderRow>
            <ZegTabs
              onTabChange={onTabChange}
              selectedTabId={selectedTabId}
              tabs={tabs}
            />
            {headerAction}
          </DesktopHeaderRow>
        )}
      </Header>
      <ContentStyled>{children}</ContentStyled>
    </Panel>
  )
}

export default ZegPanel
